import type { RouteRecordRaw, NavigationGuard } from 'vue-router';
import { requireNoSignupSubmitted, requireInitialSignupInfo } from '@/routes/guards/signup';
import {
  requireNoAuth,
  requireAuthWithoutMFAPreference,
  requireEmailLogin,
} from '@/routes/guards/auth';
import useSignupStore from './signupStore';
import claimProfileImage from './assets/claim-profile.svg';
import orgDetailsImage from './assets/org-details.svg';
import userDetailsImage from './assets/user-details.svg';

const catchSignupSource: NavigationGuard = (to, _from, next) => {
  // We assume it's rare for a new org to receive multiple signup sources (e.g. get an invite from a client
  // and a shared profile from a supplier) so no effort is made to support multiple sources and no intention
  // is made to prioritise one over the other.
  if (to.query.invite) {
    window.localStorage.setItem(
      'signupSource',
      JSON.stringify({ type: 'invite', id: to.query.invite }),
    );
  } else if (to.query.shared_profile) {
    window.localStorage.setItem(
      'signupSource',
      JSON.stringify({ type: 'shared_profile', id: to.query.shared_profile }),
    );
  } else if (to.query.trial) {
    window.localStorage.setItem(
      'signupSource',
      JSON.stringify({ type: 'trial', id: to.query.trial }),
    );
  } else if (to.query.federated_invite) {
    window.localStorage.setItem(
      'signupSource',
      JSON.stringify({ type: 'federated_invite', id: to.query.federated_invite }),
    );
  } else {
    window.localStorage.removeItem('signupSource');
  }

  next();
};

function requireSignupStep(step: number): NavigationGuard {
  return (_to, _from, next) => {
    const signupStore = useSignupStore();
    if (signupStore.unlockedStep < step) {
      next({ name: 'org-signup-claim' });
    } else {
      next();
    }
  };
}

export default [
  {
    path: '/signup/org',
    component: () => import('./components/SignupLayoutRouted.vue'),
    beforeEnter: [catchSignupSource, requireNoAuth],
    redirect: { name: 'org-signup-claim' },
    meta: { unsafeRefresh: true },
    children: [
      {
        name: 'org-signup-claim',
        path: 'claim',
        component: () => import('./components/views/ClaimProfile.vue'),
        meta: {
          rightImage: claimProfileImage,
        },
      },
      {
        name: 'org-signup-details',
        path: 'details',
        component: () => import('./components/views/OrgDetails.vue'),
        beforeEnter: [requireSignupStep(2)],
        meta: {
          rightImage: orgDetailsImage,
          rightImagePosition: 'bottom right',
        },
      },
      {
        name: 'user-signup-details',
        path: 'user',
        component: () => import('./components/views/UserDetails.vue'),
        beforeEnter: [requireSignupStep(3)],
        meta: {
          rightImage: userDetailsImage,
          rightImagePosition: 'bottom right',
          rightColor: 'blue',
        },
      },
      {
        name: 'org-signup-confirm',
        path: 'confirm',
        component: () => import('./components/views/ConfirmSignup.vue'),
        // beforeEnter: [requireSignupStep(4)],
        meta: {
          rightImage: userDetailsImage,
          rightImagePosition: 'bottom right',
          rightColor: 'blue',
        },
      },
    ],
  },
  {
    path: '/org-signup',
    component: () => import('@/legacy/components/auth/Signup.vue'),
    meta: { unsafeRefresh: true },
    children: [
      {
        name: 'org-signup-1',
        path: '',
        component: () => import('@/legacy/components/auth/OrgSignup1.vue'),
        beforeEnter: [catchSignupSource, requireNoAuth, requireNoSignupSubmitted],
      },
      {
        path: '1a',
        component: () => import('@/legacy/components/auth/OrgSignup1a.vue'),
        beforeEnter: [requireNoAuth, requireNoSignupSubmitted, requireInitialSignupInfo],
      },
      {
        path: '2',
        component: () => import('@/legacy/components/auth/OrgSignup2.vue'),
        beforeEnter: [requireNoAuth, requireNoSignupSubmitted, requireInitialSignupInfo],
      },
      {
        path: '2a',
        component: () => import('@/legacy/components/auth/PhoneAuth.vue'),
        beforeEnter: [requireNoAuth, requireNoSignupSubmitted, requireInitialSignupInfo],
        props: { organisation: true },
      },
      {
        path: 'confirm',
        component: () => import('@/legacy/components/auth/ConfirmSignup.vue'),
        beforeEnter: requireNoAuth,
      },
    ],
  },
  {
    path: '/user-signup',
    component: () => import('@/legacy/components/auth/Signup.vue'),
    beforeEnter: requireNoAuth,
    meta: { unsafeRefresh: true },
    children: [
      {
        path: '',
        component: () => import('@/legacy/components/auth/UserSignup1.vue'),
      },
      {
        path: '1',
        component: () => import('@/legacy/components/auth/PhoneAuth.vue'),
        props: { organisation: false },
      },
    ],
  },
  {
    path: '/auth',
    component: () => import('@/modules/auth/LayoutDarkLogo.vue'),
    beforeEnter: requireNoAuth,
    meta: { unsafeRefresh: true },
    children: [
      {
        path: 'login',
        name: 'login',
        // Login components are bundled together to ensure there are no refresh due to chunk errors mid login.
        component: () => import('@/legacy/components/loginComponents').then((m) => m.Login),
      },
      {
        path: 'forgot',
        name: 'forgot',
        component: () => import('@/legacy/components/auth/Forgot.vue'),
      },
      {
        path: 'reset',
        name: 'reset',
        // Login components are bundled together to ensure there are no refresh due to chunk errors mid login.
        component: () => import('@/legacy/components/loginComponents').then((m) => m.ResetPassword),
      },
      {
        path: 'mfa/sms/:mfaType',
        name: 'mfa-sms',
        component: () =>
          // Login components are bundled together to ensure there are no refresh due to chunk errors mid login.
          import('@/legacy/components/loginComponents').then((m) => m.MultiFactorAuth),
        props: (route) => ({
          mfaType: route.params.mfaType,
          flowType: route.query.flowType,
        }),
        beforeEnter: requireEmailLogin,
      },
      {
        path: 'mfa/totp/:mfaType',
        name: 'mfa-totp',
        component: () =>
          // Login components are bundled together to ensure there are no refresh due to chunk errors mid login.
          import('@/legacy/components/loginComponents').then((m) => m.MultiFactorAuth),
        props: (route) => ({
          mfaType: route.params.mfaType,
          flowType: route.query.flowType,
        }),
        beforeEnter: requireEmailLogin,
      },
    ],
  },
  {
    path: '/auth',
    beforeEnter: requireAuthWithoutMFAPreference,
    meta: { unsafeRefresh: true },
    children: [
      {
        path: 'mfa/setup',
        name: 'mfa-setup',
        component: () => import('./components/views/SetupMFA.vue'),
      },
    ],
  },
  {
    path: '/auth/org/about-you',
    name: 'signup-org-survey',
    component: () => import('./components/views/OrgSurvey.vue'),
    meta: { unsafeRefresh: true },
  },
] satisfies RouteRecordRaw[];
